import { toLower } from "lodash";
import { asTimeZone } from "../utils/dateFormatter";
import { isBooleanFalse, isBooleanTrue, isListEmpty, isListNotEmpty, isNullOrUndefined, isNumberGreaterThanOrEqualToZero, isNumberGreaterThanZero, isObjectEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../utils/objectUtils";
import Product from "./Product";
import moment from 'moment-timezone';
import Variant from "./Variant";
import { getProductObjById, getVariantObjById } from "../utils/entityUtils";
import { getMatchingCouponDiscountRow } from "../utils/couponUtils";

class CartItem {
    id = null; // id of the product
    sku = null; // sku of the product
    quantity = 0; // quantity of product per order
    variantId = null; // id of the variant of the product
    variantSku = null; // sku of the variant of the product
    bundleCount = 1;

    constructor(cartItem) {
        if (isObjectNotEmpty(cartItem)) {
            if (isStringNotEmpty(cartItem.id)) { this.id = cartItem.id; }
            if (isStringNotEmpty(cartItem.sku)) { this.sku = cartItem.sku; }
            if (isNumberGreaterThanOrEqualToZero(cartItem.quantity)) { this.quantity = cartItem.quantity; }
            if (isStringNotEmpty(cartItem.variantId)) { this.variantId = cartItem.variantId; }
            if (isStringNotEmpty(cartItem.variantSku)) { this.variantSku = cartItem.variantSku; }
            if (isNumberGreaterThanOrEqualToZero(cartItem.bundleCount)) { this.bundleCount = cartItem.bundleCount; }
        }
    }

    toDto = () => {
        return {
            id: this.id,
            sku: this.sku,
            quantity: this.quantity,
            variantId: this.variantId,
            variantSku: this.variantSku,
            bundleCount: this.bundleCount,
        };
    }

    isValid = () => {
        if (isStringEmpty(this.id)) {
            throw new Error('Product ID is required.');
        }
        if (isNullOrUndefined(this.quantity)) {
            throw new Error('Quantity is required.');
        }

        return true;
    }

    static getTotalQuantity = (cartItems) => {
        return cartItems.reduce((total, item) => total + (item.quantity * item.bundleCount), 0);
    }

    static getCartItemsTotal = (cartItems, products, variants, dontApplyToBundleCount = false) => {
        return cartItems.reduce((total, item) => total + (getVariantObjById(item?.variantId, variants)?.getPrice() ?? getProductObjById(item?.id, products)?.getPrice()) * (item.quantity * (dontApplyToBundleCount ? 1 : item.bundleCount)), 0);
    }

    static getCartItemTaxTotal = (cartItem, taxRate, products, variants) => {
        if (isObjectNotEmpty(cartItem) && isStringNotEmpty(cartItem.id)) {
            let matchingProduct = products?.find(p => p.id === cartItem.id);
            if (['TAXABLE', 'PRODUCT_ONLY'].includes(matchingProduct?.taxStatus)) {
                return (taxRate / 100) * ((getVariantObjById(cartItem?.variantId, variants)?.getPrice() ?? getProductObjById(cartItem?.id, products)?.getPrice()) * (cartItem.quantity * cartItem.bundleCount));
            }
        }

        return 0.0;
    }

    static getCartItemsTaxTotal = (cartItems, taxRate, products, variants) => {
        return cartItems.reduce((total, cartItem) => total + CartItem.getCartItemTaxTotal(cartItem, taxRate, products, variants), 0);
    }

    static getMatchingCouponDiscountForCartItem = (coupon, cartItem, products = []) => {
        if (!isObjectNotEmpty(coupon) || !isObjectNotEmpty(cartItem)) {
            return null;
        }

        const matchingProduct = products?.find((product) => product.id === cartItem?.id) ?? null;
        return getMatchingCouponDiscountRow(coupon?.discounts, {
            product: matchingProduct,
            productId: cartItem?.id ?? null,
            productSku: cartItem?.sku ?? matchingProduct?.sku ?? null,
            variantId: cartItem?.variantId ?? null,
            // titrating funnels carry no variant until prescription - without the fallback the
            // cart preview drops variant-scoped discounts the order will actually apply
            allowTargetOnlyFallback: isStringEmpty(cartItem?.variantId),
        });
    }

    static getMixedCartDiscount = (coupon, cartItems, taxRate, products, variants, applyToBundle) => {
        let discountAmount = 0.0;
        let discountTaxAmount = 0.0;

        cartItems.forEach((cartCouponItem) => {
            const cartCouponItemDiscount = CartItem.getMatchingCouponDiscountForCartItem(coupon, cartCouponItem, products);
            if (isObjectNotEmpty(cartCouponItemDiscount)) {
                const cartCouponItemSubtotal = CartItem.getCartItemsTotal([cartCouponItem], products, variants, applyToBundle);
                const cartCouponItemTax = CartItem.getCartItemsTaxTotal([cartCouponItem], taxRate, products, variants);

                if (cartCouponItemDiscount.discountType === 'FLAT_AMOUNT') {
                    if (cartCouponItemSubtotal >= cartCouponItemDiscount.discountAmount) {
                        discountAmount += cartCouponItemDiscount.discountAmount;
                        discountTaxAmount += (cartCouponItemTax - (cartCouponItemTax * ((cartCouponItemSubtotal - cartCouponItemDiscount.discountAmount) / cartCouponItemSubtotal)));
                    } else {
                        discountAmount += cartCouponItemSubtotal;
                        discountTaxAmount += cartCouponItemTax;
                    }
                } else {
                    discountAmount += (cartCouponItemDiscount.discountAmount * cartCouponItemSubtotal);
                    discountTaxAmount += (cartCouponItemDiscount.discountAmount * cartCouponItemTax);
                }
            }
        });

        return { discountTotal: discountAmount, discountTax: discountTaxAmount };
    }

    static getCartDiscount = (coupon, cartItems, taxRate, products, variants) => {
        let discountAmount = 0.0;
        let discountTaxAmount = 0.0;
        // console.log('coupon', coupon);
        // console.log('cartItems', cartItems);
        let applyToBundle = isNumberGreaterThanOrEqualToZero(cartItems?.[0]?.bundleCount) && cartItems?.[0]?.bundleCount > 1 && isBooleanTrue(coupon?.applyToBundle) && isBooleanTrue(['RECURRING', 'BUNDLE', 'MIXED_RECURRING'].includes(coupon?.discountTarget));

        let cartTotal = CartItem.getCartItemsTotal(cartItems, products, variants, applyToBundle);
        let cartTax = CartItem.getCartItemsTaxTotal(cartItems, taxRate, products, variants);
        if (isObjectNotEmpty(coupon) && isNumberGreaterThanZero(cartTotal)) {
            if (isBooleanTrue(coupon.active)) {
                if (coupon.usageLimit === 0 || (coupon.usageLimit > 0 && coupon.usageCount < coupon.usageLimit)) {
                    // console.log('getCartDiscount: usageLimit good');
                    if (isNullOrUndefined(coupon.expiresAt) || (asTimeZone(coupon.expiresAt, 'US/Pacific') > moment.tz('US/Pacific'))) {
                        // console.log('getCartDiscount: expiresAt good');
                        if (isListEmpty(coupon.excludedProductIds) || (isListNotEmpty(coupon.excludedProductIds) && !cartItems.some(i => coupon.excludedProductIds.includes(i.id)))) {
                            // console.log('getCartDiscount: excluded products good');
                            if (isListEmpty(coupon.excludedVariantIds) || (isListNotEmpty(coupon.excludedVariantIds) && !cartItems.some(i => coupon.excludedVariantIds.includes(i.variantId)))) {
                                // console.log('getCartDiscount: excluded variants good');
                                if ((isBooleanFalse(coupon.applyToBundle) && !cartItems.some(i => i.bundleCount > 1)) || (isBooleanTrue(coupon.applyToBundle) && (isListEmpty(coupon.excludedBundleCounts) || (isListNotEmpty(coupon.excludedBundleCounts) && !cartItems.some(i => coupon.excludedBundleCounts.includes(i.bundleCount)))))) {
                                    // console.log('getCartDiscount: apply to bundle good');
                                    if (coupon.discountTarget === 'CART') {
                                        if (coupon.discountType === 'FLAT_AMOUNT') {
                                            discountAmount = coupon.discountAmount;

                                            // calculate tax reduction for discount across all taxable cartItems
                                            // spread discount across cartItems and then reduce tax accordingly for taxable items
                                            discountTaxAmount = cartTax - (cartTax * ((cartTotal - coupon.discountAmount) / cartTotal));
                                        } else {
                                            discountAmount = coupon.discountAmount * cartTotal;

                                            // calculate tax reduction for discount across all taxable cartItems
                                            discountTaxAmount = coupon.discountAmount * cartTax;
                                        }
                                    } else if ((coupon.discountTarget === 'PRODUCT') && isListNotEmpty(coupon.productIds)) {
                                        let cartCouponItems = cartItems.filter(i => coupon.productIds.includes(i.id));
                                        if (isListNotEmpty(cartCouponItems)) {
                                            // cart contains at least some of the product(s) that this discount applies to
                                            let cartCouponItemSubtotal = CartItem.getCartItemsTotal(cartCouponItems, products, variants, applyToBundle);
                                            let cartCouponItemTax = CartItem.getCartItemsTaxTotal(cartCouponItems, taxRate, products, variants);
                                            // console.log(cartCouponItemSubtotal);
                                            if (coupon.discountType === 'FLAT_AMOUNT') {
                                                if (cartCouponItemSubtotal >= coupon.discountAmount) {
                                                    discountAmount = coupon.discountAmount;

                                                    discountTaxAmount = cartCouponItemTax - (cartCouponItemTax * ((cartCouponItemSubtotal - coupon.discountAmount) / cartCouponItemSubtotal));
                                                } else {
                                                    // if discount is larger than cart total, cap discount at cart total
                                                    discountAmount = cartCouponItemSubtotal;

                                                    discountTaxAmount = cartCouponItemTax;
                                                }
                                            } else {
                                                discountAmount = coupon.discountAmount * cartCouponItemSubtotal;

                                                discountTaxAmount = coupon.discountAmount * cartCouponItemTax;
                                            }
                                        }
                                    } else if ((coupon.discountTarget === 'MIXED' || coupon.discountTarget === 'MIXED_RECURRING') && isListNotEmpty(coupon.discounts)) {
                                        let cartCouponItems = cartItems.filter(i => isObjectNotEmpty(CartItem.getMatchingCouponDiscountForCartItem(coupon, i, products)));
                                        if (isListNotEmpty(cartCouponItems)) {
                                            const mixedDiscount = CartItem.getMixedCartDiscount(coupon, cartCouponItems, taxRate, products, variants, applyToBundle);
                                            discountAmount = mixedDiscount.discountTotal;
                                            discountTaxAmount = mixedDiscount.discountTax;
                                        }
                                    } else if (coupon.discountTarget === 'RECURRING') {
                                        if (coupon.discountType === 'FLAT_AMOUNT') {
                                            discountAmount = coupon.discountAmount;

                                            // calculate tax reduction for discount across all taxable cartItems
                                            // spread discount across cartItems and then reduce tax accordingly for taxable items
                                            discountTaxAmount = cartTax - (cartTax * ((cartTotal - coupon.discountAmount) / cartTotal));
                                        } else {
                                            discountAmount = coupon.discountAmount * cartTotal;

                                            // calculate tax reduction for discount across all taxable cartItems
                                            discountTaxAmount = coupon.discountAmount * cartTax;
                                        }
                                    } else if ((coupon.discountTarget === 'BUNDLE') && isListNotEmpty(coupon.productIds)) {
                                        let cartCouponItems = cartItems.filter(i => coupon.productIds.includes(i.id));
                                        if (isListNotEmpty(cartCouponItems)) {
                                            // cart contains at least some of the product(s) that this discount applies to
                                            let cartCouponItemSubtotal = CartItem.getCartItemsTotal(cartCouponItems, products, variants, false);
                                            let cartCouponItemTax = CartItem.getCartItemsTaxTotal(cartCouponItems, taxRate, products, variants);
                                            // console.log('cartCouponItemSubtotal', cartCouponItemSubtotal);
                                            if (coupon.discountType === 'FLAT_AMOUNT') {
                                                let bundleDiscountAmount = cartCouponItems.reduce((total, item) => total + coupon.discountAmount * (item.quantity * item.bundleCount), 0);
                                                // console.log('bundleDiscountAmount', bundleDiscountAmount);

                                                if (cartCouponItemSubtotal >= bundleDiscountAmount) {
                                                    discountAmount = bundleDiscountAmount;

                                                    discountTaxAmount = cartCouponItemTax - (cartCouponItemTax * ((cartCouponItemSubtotal - bundleDiscountAmount) / cartCouponItemSubtotal));
                                                } else {
                                                    // if discount is larger than cart total, cap discount at cart total
                                                    discountAmount = cartCouponItemSubtotal;

                                                    discountTaxAmount = cartCouponItemTax;
                                                }
                                            } else {
                                                discountAmount = coupon.discountAmount * cartCouponItemSubtotal;

                                                discountTaxAmount = coupon.discountAmount * cartCouponItemTax;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        return { discountTotal: discountAmount, discountTax: discountTaxAmount };
    }

    static getPartialCartDiscount = (coupon, allCartItems, cartItems, taxRate, products, variants) => {
        let discountAmount = 0.0;
        let discountTaxAmount = 0.0;
        // console.log(coupon);
        let applyToBundle = isNumberGreaterThanOrEqualToZero(cartItems?.[0]?.bundleCount) && cartItems?.[0]?.bundleCount > 1 && isBooleanTrue(coupon?.applyToBundle) && isBooleanTrue(['RECURRING', 'BUNDLE', 'MIXED_RECURRING'].includes(coupon?.discountTarget));

        let partialCartTotal = CartItem.getCartItemsTotal(cartItems, products, variants, applyToBundle);
        let partialCartTax = CartItem.getCartItemsTaxTotal(cartItems, taxRate, products, variants);
        let cartTotal = CartItem.getCartItemsTotal(allCartItems, products, variants, applyToBundle);
        let cartTax = CartItem.getCartItemsTaxTotal(allCartItems, taxRate, products, variants);
        if (isObjectNotEmpty(coupon) && isNumberGreaterThanZero(cartTotal)) {
            if (isBooleanTrue(coupon.active)) {
                if (coupon.usageLimit === 0 || (coupon.usageLimit > 0 && coupon.usageCount < coupon.usageLimit)) {
                    // console.log('getCartDiscount: usageLimit good');
                    if (isNullOrUndefined(coupon.expiresAt) || (asTimeZone(coupon.expiresAt, 'US/Pacific') > moment.tz('US/Pacific'))) {
                        // console.log('getCartDiscount: expiresAt good');
                        if (isListEmpty(coupon.excludedProductIds) || (isListNotEmpty(coupon.excludedProductIds) && !cartItems.some(i => coupon.excludedProductIds.includes(i.product?.id)))) {
                            // console.log('getCartDiscount: excluded products good');
                            if (isListEmpty(coupon.excludedVariantIds) || (isListNotEmpty(coupon.excludedVariantIds) && !cartItems.some(i => coupon.excludedVariantIds.includes(i.variantId)))) {
                                // console.log('getCartDiscount: excluded variants good');
                                if ((isBooleanFalse(coupon.applyToBundle) && !cartItems.some(i => i.bundleCount > 1)) || (isBooleanTrue(coupon.applyToBundle) && (isListEmpty(coupon.excludedBundleCounts) || (isListNotEmpty(coupon.excludedBundleCounts) && !cartItems.some(i => coupon.excludedBundleCounts.includes(i.bundleCount)))))) {
                                    // console.log('getCartDiscount: apply to bundle good');
                                    if (coupon.discountTarget === 'CART') {
                                        if (coupon.discountType === 'FLAT_AMOUNT') {
                                            let partialCouponDiscountAmount = coupon.discountAmount * (partialCartTotal / cartTotal);
                                            discountAmount = partialCouponDiscountAmount;

                                            // calculate tax reduction for discount across all taxable cartItems
                                            // spread discount across cartItems and then reduce tax accordingly for taxable items
                                            discountTaxAmount = partialCartTax - (partialCartTax * ((partialCartTotal - partialCouponDiscountAmount) / partialCartTotal));
                                        } else {
                                            discountAmount = coupon.discountAmount * partialCartTotal;

                                            // calculate tax reduction for discount across all taxable cartItems
                                            discountTaxAmount = coupon.discountAmount * partialCartTax;
                                        }
                                    } else if ((coupon.discountTarget === 'PRODUCT') && isListNotEmpty(coupon.productIds)) {
                                        let cartCouponItems = cartItems.filter(i => coupon.productIds.includes(i.id));
                                        if (isListNotEmpty(cartCouponItems)) {
                                            // cart contains at least some of the product(s) that this discount applies to
                                            let cartCouponItemSubtotal = CartItem.getCartItemsTotal(cartCouponItems, products, variants, applyToBundle);
                                            let cartCouponItemTax = CartItem.getCartItemsTaxTotal(cartCouponItems, taxRate, products, variants);
                                            // console.log(cartCouponItemSubtotal);
                                            if (coupon.discountType === 'FLAT_AMOUNT') {
                                                let partialCouponDiscountAmount = coupon.discountAmount * (cartCouponItemSubtotal / cartTotal);
                                                if (cartCouponItemSubtotal >= partialCouponDiscountAmount) {
                                                    discountAmount = partialCouponDiscountAmount;

                                                    discountTaxAmount = cartCouponItemTax - (cartCouponItemTax * ((cartCouponItemSubtotal - partialCouponDiscountAmount) / cartCouponItemSubtotal));
                                                } else {
                                                    // if discount is larger than cart total, cap discount at cart total
                                                    discountAmount = cartCouponItemSubtotal;

                                                    discountTaxAmount = cartCouponItemTax;
                                                }
                                            } else {
                                                discountAmount = coupon.discountAmount * cartCouponItemSubtotal;

                                                discountTaxAmount = coupon.discountAmount * cartCouponItemTax;
                                            }
                                        }
                                    } else if ((coupon.discountTarget === 'MIXED' || coupon.discountTarget === 'MIXED_RECURRING') && isListNotEmpty(coupon.discounts)) {
                                        let cartCouponItems = cartItems.filter(i => isObjectNotEmpty(CartItem.getMatchingCouponDiscountForCartItem(coupon, i, products)));
                                        if (isListNotEmpty(cartCouponItems)) {
                                            const mixedDiscount = CartItem.getMixedCartDiscount(coupon, cartCouponItems, taxRate, products, variants, applyToBundle);
                                            discountAmount = mixedDiscount.discountTotal;
                                            discountTaxAmount = mixedDiscount.discountTax;
                                        }
                                    } else if (coupon.discountTarget === 'RECURRING') {
                                        if (coupon.discountType === 'FLAT_AMOUNT') {
                                            let partialCouponDiscountAmount = coupon.discountAmount * (partialCartTotal / cartTotal);

                                            discountAmount = partialCouponDiscountAmount;

                                            // calculate tax reduction for discount across all taxable cartItems
                                            // spread discount across cartItems and then reduce tax accordingly for taxable items
                                            discountTaxAmount = partialCartTax - (partialCartTax * ((partialCartTotal - partialCouponDiscountAmount) / partialCartTotal));
                                        } else {
                                            discountAmount = coupon.discountAmount * partialCartTotal;

                                            // calculate tax reduction for discount across all taxable cartItems
                                            discountTaxAmount = coupon.discountAmount * partialCartTax;
                                        }
                                    } else if ((coupon.discountTarget === 'BUNDLE') && isListNotEmpty(coupon.productIds)) {
                                        let cartCouponItems = cartItems.filter(i => coupon.productIds.includes(i.id));
                                        if (isListNotEmpty(cartCouponItems)) {
                                            // cart contains at least some of the product(s) that this discount applies to
                                            let cartCouponItemSubtotal = CartItem.getCartItemsTotal(cartCouponItems, products, variants, false);
                                            let cartCouponItemTax = CartItem.getCartItemsTaxTotal(cartCouponItems, taxRate, products, variants);
                                            // console.log(cartCouponItemSubtotal);
                                            if (coupon.discountType === 'FLAT_AMOUNT') {
                                                let bundleDiscountAmount = cartCouponItems.reduce((total, item) => total + (coupon.discountAmount * item.quantity * item.bundleCount), 0);
                                                if (cartCouponItemSubtotal >= bundleDiscountAmount) {
                                                    discountAmount = bundleDiscountAmount;

                                                    discountTaxAmount = cartCouponItemTax - (cartCouponItemTax * ((cartCouponItemSubtotal - bundleDiscountAmount) / cartCouponItemSubtotal));
                                                } else {
                                                    // if discount is larger than cart total, cap discount at cart total
                                                    discountAmount = cartCouponItemSubtotal;

                                                    discountTaxAmount = cartCouponItemTax;
                                                }
                                            } else {
                                                discountAmount = coupon.discountAmount * cartCouponItemSubtotal;

                                                discountTaxAmount = coupon.discountAmount * cartCouponItemTax;
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        return { discountTotal: discountAmount, discountTax: discountTaxAmount };
    }

    static getCartItemDiscount = (coupon, allCartItems, cartItemToCheck, taxRate, products, variants) => {
        let discountAmount = 0.0;
        let discountTaxAmount = 0.0;
        // console.log(coupon, cartItemToCheck);
        if (isObjectNotEmpty(cartItemToCheck)) {
            let cartItems = [cartItemToCheck];
            let applyToBundle = isNumberGreaterThanOrEqualToZero(cartItems?.[0]?.bundleCount) && cartItems?.[0]?.bundleCount > 1 && isBooleanTrue(coupon?.applyToBundle) && isBooleanTrue(['RECURRING', 'BUNDLE', 'MIXED_RECURRING'].includes(coupon?.discountTarget));

            let partialCartTotal = CartItem.getCartItemsTotal(cartItems, products, variants, applyToBundle);
            let partialCartTax = CartItem.getCartItemsTaxTotal(cartItems, taxRate, products, variants);
            let cartTotal = CartItem.getCartItemsTotal(allCartItems, products, variants, applyToBundle);
            let cartTax = CartItem.getCartItemsTaxTotal(allCartItems, taxRate, products, variants);
            if (isObjectNotEmpty(coupon) && isNumberGreaterThanZero(cartTotal)) {
                if (isBooleanTrue(coupon.active)) {
                    if (coupon.usageLimit === 0 || (coupon.usageLimit > 0 && coupon.usageCount < coupon.usageLimit)) {
                        // console.log('getCartDiscount: usageLimit good');
                        if (isNullOrUndefined(coupon.expiresAt) || (asTimeZone(coupon.expiresAt, 'US/Pacific') > moment.tz('US/Pacific'))) {
                            // console.log('getCartDiscount: expiresAt good');
                            if (isListEmpty(coupon.excludedProductIds) || (isListNotEmpty(coupon.excludedProductIds) && !cartItems.some(i => coupon.excludedProductIds.includes(i.id)))) {
                                // console.log('getCartDiscount: excluded products good');
                                if (isListEmpty(coupon.excludedVariantIds) || (isListNotEmpty(coupon.excludedVariantIds) && !cartItems.some(i => coupon.excludedVariantIds.includes(i.variantId)))) {
                                    // console.log('getCartDiscount: excluded variants good');
                                    if ((isBooleanFalse(coupon.applyToBundle) && !cartItems.some(i => i.bundleCount > 1)) || (isBooleanTrue(coupon.applyToBundle) && (isListEmpty(coupon.excludedBundleCounts) || (isListNotEmpty(coupon.excludedBundleCounts) && !cartItems.some(i => coupon.excludedBundleCounts.includes(i.bundleCount)))))) {
                                        // console.log('getCartDiscount: apply to bundle good');
                                        if (coupon.discountTarget === 'CART') {
                                            if (coupon.discountType === 'FLAT_AMOUNT') {
                                                let partialCouponDiscountAmount = coupon.discountAmount * (partialCartTotal / cartTotal);
                                                discountAmount = partialCouponDiscountAmount;

                                                // calculate tax reduction for discount across all taxable cartItems
                                                // spread discount across cartItems and then reduce tax accordingly for taxable items
                                                discountTaxAmount = partialCartTax - (partialCartTax * ((partialCartTotal - partialCouponDiscountAmount) / partialCartTotal));
                                            } else {
                                                discountAmount = coupon.discountAmount * partialCartTotal;

                                                // calculate tax reduction for discount across all taxable cartItems
                                                discountTaxAmount = coupon.discountAmount * partialCartTax;
                                            }
                                        } else if ((coupon.discountTarget === 'PRODUCT') && isListNotEmpty(coupon.productIds)) {
                                            let cartCouponItems = cartItems.filter(i => coupon.productIds.includes(i.id));
                                            if (isListNotEmpty(cartCouponItems)) {
                                                // cart contains at least some of the product(s) that this discount applies to
                                                let cartCouponItemSubtotal = CartItem.getCartItemsTotal(cartCouponItems, products, variants, applyToBundle);
                                                let cartCouponItemTax = CartItem.getCartItemsTaxTotal(cartCouponItems, taxRate, products, variants);
                                                // console.log(cartCouponItemSubtotal);
                                                if (coupon.discountType === 'FLAT_AMOUNT') {
                                                    let partialCouponDiscountAmount = coupon.discountAmount * (cartCouponItemSubtotal / cartTotal);
                                                    if (cartCouponItemSubtotal >= partialCouponDiscountAmount) {
                                                        discountAmount = partialCouponDiscountAmount;

                                                        discountTaxAmount = cartCouponItemTax - (cartCouponItemTax * ((cartCouponItemSubtotal - partialCouponDiscountAmount) / cartCouponItemSubtotal));
                                                    } else {
                                                        // if discount is larger than cart total, cap discount at cart total
                                                        discountAmount = cartCouponItemSubtotal;

                                                        discountTaxAmount = cartCouponItemTax;
                                                    }
                                                } else {
                                                    discountAmount = coupon.discountAmount * cartCouponItemSubtotal;

                                                    discountTaxAmount = coupon.discountAmount * cartCouponItemTax;
                                                }
                                            }
                                        } else if ((coupon.discountTarget === 'MIXED' || coupon.discountTarget === 'MIXED_RECURRING') && isListNotEmpty(coupon.discounts)) {
                                            let cartCouponItems = cartItems.filter(i => isObjectNotEmpty(CartItem.getMatchingCouponDiscountForCartItem(coupon, i, products)));
                                            if (isListNotEmpty(cartCouponItems)) {
                                                const mixedDiscount = CartItem.getMixedCartDiscount(coupon, cartCouponItems, taxRate, products, variants, applyToBundle);
                                                discountAmount = mixedDiscount.discountTotal;
                                                discountTaxAmount = mixedDiscount.discountTax;
                                            }
                                        } else if (coupon.discountTarget === 'RECURRING') {
                                            if (coupon.discountType === 'FLAT_AMOUNT') {
                                                let partialCouponDiscountAmount = coupon.discountAmount * (partialCartTotal / cartTotal);

                                                discountAmount = partialCouponDiscountAmount;

                                                // calculate tax reduction for discount across all taxable cartItems
                                                // spread discount across cartItems and then reduce tax accordingly for taxable items
                                                discountTaxAmount = partialCartTax - (partialCartTax * ((partialCartTotal - partialCouponDiscountAmount) / partialCartTotal));
                                            } else {
                                                discountAmount = coupon.discountAmount * partialCartTotal;

                                                // calculate tax reduction for discount across all taxable cartItems
                                                discountTaxAmount = coupon.discountAmount * partialCartTax;
                                            }
                                        } else if ((coupon.discountTarget === 'BUNDLE') && isListNotEmpty(coupon.productIds)) {
                                            let cartCouponItems = cartItems.filter(i => coupon.productIds.includes(i.id));
                                            if (isListNotEmpty(cartCouponItems)) {
                                                // cart contains at least some of the product(s) that this discount applies to
                                                let cartCouponItemSubtotal = CartItem.getCartItemsTotal(cartCouponItems, products, variants, false);
                                                let cartCouponItemTax = CartItem.getCartItemsTaxTotal(cartCouponItems, taxRate, products, variants);
                                                // console.log(cartCouponItemSubtotal);
                                                if (coupon.discountType === 'FLAT_AMOUNT') {
                                                    let bundleDiscountAmount = cartCouponItems.reduce((total, item) => total + (coupon.discountAmount * item.quantity * item.bundleCount), 0);
                                                    if (cartCouponItemSubtotal >= bundleDiscountAmount) {
                                                        discountAmount = bundleDiscountAmount;

                                                        discountTaxAmount = cartCouponItemTax - (cartCouponItemTax * ((cartCouponItemSubtotal - bundleDiscountAmount) / cartCouponItemSubtotal));
                                                    } else {
                                                        // if discount is larger than cart total, cap discount at cart total
                                                        discountAmount = cartCouponItemSubtotal;

                                                        discountTaxAmount = cartCouponItemTax;
                                                    }
                                                } else {
                                                    discountAmount = coupon.discountAmount * cartCouponItemSubtotal;

                                                    discountTaxAmount = coupon.discountAmount * cartCouponItemTax;
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        return { discountTotal: discountAmount, discountTax: discountTaxAmount };
    }

    static getCartShipping = (shippingMethod, cartItems, taxRate, products, variants) => {
        let shippingTaxAmount = 0.0;
        let shippingAmount = 0.0;
        if (isObjectNotEmpty(shippingMethod)) {
            let shippingMethodTaxable = shippingMethod.taxStatus === 'TAXABLE';
            let shippingMethodCalculationType = shippingMethod.calculationType; // ORDER or CLASS
            if (shippingMethodCalculationType === 'ORDER') {
                let totalQuantity = CartItem.getTotalQuantity(cartItems);
                let shippingMethodCost = shippingMethod.cost;
                let shippingMethodTax = 0.0;
                if (!isNaN(shippingMethodCost)) {
                    shippingMethodCost = Number(shippingMethod.cost);
                } else if (isNaN(shippingMethodCost) && isStringNotEmpty(shippingMethodCost)) {
                    // shipping method cost is a formula;
                    let costFormula = shippingMethodCost.replace('[qty]', totalQuantity);
                    shippingMethodCost = Number(Function("return " + costFormula));
                }

                for (const item of cartItems) {
                    let cartItemShippingTax = 0;
                    let cartItemShippingTotal = totalQuantity > 0 ? shippingMethodCost * ((item.quantity * item.bundleCount) / totalQuantity) : 0.0;
                    let matchingProduct = products?.find(p => p.id === item.id);
                    if (isObjectNotEmpty(matchingProduct)) {
                        if (['TAXABLE', 'SHIPPING_ONLY'].includes(matchingProduct.taxStatus)) {
                            cartItemShippingTax = (taxRate / 100) * cartItemShippingTotal;
                        }
                    }

                    shippingMethodTax = shippingMethodTax + cartItemShippingTax;
                }

                shippingAmount = shippingMethodCost;
                if (isBooleanTrue(shippingMethodTaxable)) {
                    shippingTaxAmount = shippingMethodTax;
                }
            } else {
                // calculation type of class
                let shippingMethodCost = 0;
                let shippingMethodTax = 0;
                for (const shippingClassCost of shippingMethod.shippingClassCosts) {
                    let matchingProductIds = products.filter(p => p.shippingClassId === shippingClassCost.shippingClassId).map(p => p.id);
                    let matchingCartItems = cartItems.filter(i => matchingProductIds.includes(i.id));
                    if (isListNotEmpty(matchingCartItems)) {
                        let shippingMethodClassCost = shippingClassCost.cost;
                        let matchingQuantity = CartItem.getTotalQuantity(matchingCartItems);
                        if (!isNaN(shippingMethodClassCost)) {
                            shippingMethodClassCost = Number(shippingClassCost.cost);
                        } else if (isNaN(shippingMethodClassCost) && isStringNotEmpty(shippingMethodClassCost)) {
                            // shipping method cost is a formula;
                            let costFormula = shippingMethodClassCost.replace('[qty]', matchingQuantity);
                            shippingMethodClassCost = Number(Function("return " + costFormula));
                        }

                        for (const item of matchingCartItems) {
                            let cartItemShippingTax = 0;
                            let cartItemShippingTotal = matchingQuantity > 0 ? shippingMethodClassCost * ((item.quantity * item.bundleCount) / matchingQuantity) : 0.0;

                            let matchingProduct = products?.find(p => p.id === item.id);
                            if (isObjectNotEmpty(matchingProduct)) {
                                if (['TAXABLE', 'SHIPPING_ONLY'].includes(matchingProduct.taxStatus)) {
                                    cartItemShippingTax = (taxRate / 100) * cartItemShippingTotal;
                                }
                            }

                            shippingMethodTax = shippingMethodTax + cartItemShippingTax;
                        }

                        shippingMethodCost = shippingMethodCost + shippingMethodClassCost;
                    }
                }

                shippingAmount = shippingMethodCost;
                if (isBooleanTrue(shippingMethodTaxable)) {
                    shippingTaxAmount = shippingMethodTax;
                }
            }
        }

        return { shippingTotal: shippingAmount, shippingTax: shippingTaxAmount };
    }

    static calculateShippingMethodCost = (shippingMethod, cartItems, taxRate, products, variants) => {
        let shippingTaxAmount = 0.0;
        let shippingAmount = 0.0;
        if (isObjectNotEmpty(shippingMethod) && isListNotEmpty(cartItems) && isNumberGreaterThanOrEqualToZero(taxRate)) {
            let shippingMethodTaxable = shippingMethod.taxStatus === 'TAXABLE';
            let shippingMethodCalculationType = shippingMethod.calculationType; // ORDER or CLASS
            if (shippingMethodCalculationType === 'ORDER') {
                let totalQuantity = CartItem.getTotalQuantity(cartItems);
                let shippingMethodCost = shippingMethod.cost;
                let shippingMethodTax = 0.0;

                if (!isNaN(shippingMethodCost)) {
                    shippingMethodCost = Number(shippingMethod.cost);
                } else if (isNaN(shippingMethodCost) && isStringNotEmpty(shippingMethodCost)) {
                    // shipping method cost is a formula;
                    let costFormula = shippingMethodCost.replace('[qty]', totalQuantity);
                    shippingMethodCost = Function("return " + costFormula);
                }

                for (const item of cartItems) {
                    let cartItemShippingTax = 0;
                    let cartItemShippingTotal = totalQuantity > 0 ? shippingMethodCost * ((item.quantity * item.bundleCount) / totalQuantity) : 0.0;
                    let matchingProduct = products?.find(p => p.id === item.id);
                    if (isObjectNotEmpty(matchingProduct)) {
                        if (['TAXABLE', 'SHIPPING_ONLY'].includes(matchingProduct?.taxStatus)) {
                            cartItemShippingTax = (taxRate / 100) * cartItemShippingTotal;
                        }
                    }

                    shippingMethodTax = shippingMethodTax + cartItemShippingTax;
                }

                shippingAmount = shippingMethodCost;
                if (isBooleanTrue(shippingMethodTaxable)) {
                    shippingTaxAmount = shippingMethodTax;
                }
            } else {
                // calculation type of class
                let shippingMethodCost = 0;
                let shippingMethodTax = 0;
                for (const shippingClassCost of shippingMethod.shippingClassCosts) {
                    let matchingProductIds = products.filter(p => p.shippingClassId === shippingClassCost.shippingClassId).map(p => p.id);
                    let matchingCartItems = cartItems.filter(i => matchingProductIds.includes(i.id));
                    if (isListNotEmpty(matchingCartItems)) {
                        let shippingMethodClassCost = shippingClassCost.cost;
                        let matchingQuantity = CartItem.getTotalQuantity(matchingCartItems);

                        if (!isNaN(shippingMethodClassCost)) {
                            shippingMethodClassCost = Number(shippingClassCost.cost);
                        } else if (isNaN(shippingMethodClassCost) && isStringNotEmpty(shippingMethodClassCost)) {
                            // shipping method cost is a formula;
                            let costFormula = shippingMethodClassCost.replace('[qty]', matchingQuantity);
                            shippingMethodClassCost = Function("return " + costFormula);
                        }

                        for (const item of matchingCartItems) {
                            let cartItemShippingTax = 0;
                            let cartItemShippingTotal = matchingQuantity > 0 ? shippingMethodClassCost * ((item.quantity * item.bundleCount) / matchingQuantity) : 0.0;
                            let matchingProduct = products?.find(p => p.id === item.id);
                            if (isObjectNotEmpty(matchingProduct)) {
                                if (['TAXABLE', 'SHIPPING_ONLY'].includes(matchingProduct?.taxStatus)) {
                                    cartItemShippingTax = (taxRate / 100) * cartItemShippingTotal;
                                }
                            }

                            shippingMethodTax = shippingMethodTax + cartItemShippingTax;
                        }

                        shippingMethodCost = shippingMethodCost + shippingMethodClassCost;
                    }
                }

                shippingAmount = shippingMethodCost;
                if (isBooleanTrue(shippingMethodTaxable)) {
                    shippingTaxAmount = shippingMethodTax;
                }
            }
        }

        return { shippingTotal: shippingAmount, shippingTax: shippingTaxAmount };
    }

    static getDiscountLinesTotal = (discountLines) => {
        return discountLines.reduce((total, line) => total + line.discountTotal, 0);
    }

    static getDiscountLinesTax = (discountLines) => {
        return discountLines.reduce((total, line) => total + line.discountTax, 0);
    }

    static getFeeLinesTotal = (feeLines) => {
        return feeLines.reduce((total, line) => total + line.subtotal, 0);
    }

    static getFeeLinesTax = (feeLines) => {
        return feeLines.reduce((total, line) => total + line.tax, 0);
    }

    static getShippingLinesTotal = (shippingLines) => {
        return shippingLines.reduce((total, line) => total + line.subtotal, 0);
    }

    static getShippingLinesTax = (shippingLines) => {
        return shippingLines.reduce((total, line) => total + line.tax, 0);
    }
};

export default CartItem;
